import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Order, OrderBy } from '@templates/approval/contexts/approvalContext/types'
import { api } from 'api'
import { showSnackbar } from '../snackbar/snackbar.actions'
import { Submission } from '../types'
import { RootState } from '@redux/store'

interface Args {
  showApprovalRequests: boolean
  template?: {
    id: number
  }
}

const isApprovalError = (err: any): boolean =>
  Number(err?.status) >= 500 && err?.message !== 'Query was cancelled by React Query'
/**
 * Reusable hook that can be used to fetch approval requests
 *
 * @param {boolean} showApprovalRequests - Checks to determine if approval requests should be shown to user
 * @param {unknown} template - If defined, approval requests will only be fetched for this template
 * @returns Object with these properties:
 *  approvalRequestsLoading, approvalRequests, approvalRequestsCount, setApprovalRequestsCount, fetchApprovalRequests
 */
export const useFetchApprovalRequests = ({ showApprovalRequests, template }: Args) => {
  const aimaListSearchFeature = useSelector(
    (state: RootState) => state.Auth.features?.['aimia-list-search'],
  )
  const location = useLocation()
  const dispatch = useDispatch()
  const [approvalRequestsCount, setApprovalRequestsCount] = useState<number>(0)

  const isSubmissionsRoute = location.pathname.includes('/codes/')
  const isTemplateDataRoute = location.pathname.includes('/template/')
  const AIMIA_LIST_SEARCH_ENABLED = Boolean(aimaListSearchFeature)

  const { data, isInitialLoading: isLoading } = useQuery({
    queryKey: [
      'fetchApprovals',
      {
        orderBy: OrderBy.CREATED_AT,
        order: Order.DESC,
        offset: 0,
      },
    ],
    queryFn: ({ signal }) =>
      fetchApprovalRequests({
        orderBy: OrderBy.CREATED_AT,
        order: Order.DESC,
        offset: 0,
        signal,
      }),
    refetchOnWindowFocus: false,
  })

  async function fetchApprovalRequests({
    orderBy,
    order,
    offset,
    signal,
  }: {
    orderBy: OrderBy
    order: Order
    offset: number
    signal?: AbortSignal | undefined
  }): Promise<Submission[]> {
    if ((!isSubmissionsRoute && !isTemplateDataRoute) || !showApprovalRequests) return []
    if (!template && approvalRequestsCount > 0) return []

    const { CancelToken } = axios
    const source = CancelToken.source()

    signal?.addEventListener('abort', () => {
      source.cancel('Query was cancelled by React Query')
    })

    const params = [
      `order_by=${orderBy}`,
      `order=${order}`,
      `offset=${offset}`,
      'awaiting_approval=true',
    ]

    if (template) {
      params.unshift(`template_id=${template?.id}`)
    }

    const url = AIMIA_LIST_SEARCH_ENABLED
      ? `/api/core/v1/approvals/search?${params.join('&')}`
      : `/api/v1/account/dataset/list-search?${params.join('&')}`

    try {
      const { data } = await api.get<Submission[]>(url, {
        cancelToken: source.token,
      })
      setApprovalRequestsCount(data?.length)
      return data
    } catch (err) {
      if (!isApprovalError(err)) return []
      dispatch(
        showSnackbar({
          message: 'Failed to load approval requests',
          type: 'ERROR',
        }),
      )
      return []
    }
  }

  return {
    approvalRequestsLoading: isLoading,
    approvalRequests: data,
    approvalRequestsCount: isLoading ? 0 : data?.length,
    setApprovalRequestsCount,
    fetchApprovalRequests,
  }
}
